import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OfferCard from "@/components/OfferCard";
import ShareSheet from "@/components/ShareSheet";
import Confetti from "@/components/Confetti";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PiggyBank, QrCode, Heart, Ticket, CheckCircle2, Loader2, Store, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Eye, X, ShieldAlert, Share2, Clock, Tag } from "lucide-react";
import { euro, relativeDate } from "@/lib/format";
import { toast } from "sonner";

const expiryLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return "Scaduto";
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `Scade il ${d.toLocaleDateString("it-IT", { day: "numeric", month: "short" })}`;
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return `Scade tra ${hours}h`;
  return `Scade tra ${Math.max(1, Math.floor(ms / 60000))}m`;
};

const isExpired = (c) => {
  const t = c?.expires_at ? new Date(c.expires_at).getTime() : 0;
  return t > 0 && t <= Date.now();
};

const couponValue = (c) => {
  if (!c) return "";
  const t = c.discount_type === "amount" ? "fixed" : (c.discount_type || "percent");
  const v = Number(c.discount_value ?? c.discount ?? 0);
  if (t === "bogo") return "2x1";
  if (t === "price") return v > 0 ? `${Number.isInteger(v) ? v : v.toFixed(2)}€` : "";
  if (t === "fixed") return v > 0 ? `-${Number.isInteger(v) ? v : v.toFixed(2)}€` : "";
  return v > 0 ? `-${Math.round(v)}%` : "";
};

const MILESTONES = [10, 25, 50, 100, 150, 200, 300, 500, 750, 1000];
function savingsGoal(total) {
  let prev = 0;
  for (const m of MILESTONES) {
    if (m > total) return { goal: m, prev };
    prev = m;
  }
  const goal = Math.ceil((total + 1) / 500) * 500;
  return { goal, prev: goal - 500 };
}
function reachedMilestone(total) {
  let r = 0;
  for (const m of MILESTONES) if (m <= total) r = m;
  return r;
}

export default function MyCoupons() {
  const navigate = useNavigate();
  const { refreshClient, client, ready } = useAuth();
  const [saved, setSaved] = useState([]);
  const [claims, setClaims] = useState([]);
  const [savings, setSavings] = useState({ total_saved: 0, operations: 0 });
  const [loading, setLoading] = useState(true);
  const [celebrate, setCelebrate] = useState(false);
  const [couponQ, setCouponQ] = useState("");
  const [showUsed, setShowUsed] = useState(false);
  const [showUnused, setShowUnused] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [sortMode, setSortMode] = useState("store");
  const [suspendedUntil, setSuspendedUntil] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);

  useEffect(() => {
    if (selectedStore && !claims.some((c) => (c.vendor_id || c.store_name || "unknown") === selectedStore)) setSelectedStore(null);
  }, [claims, selectedStore]);

  useEffect(() => {
    if (loading) return;
    const reached = reachedMilestone(savings.total_saved);
    const key = "kn_savings_milestone";
    const stored = localStorage.getItem(key);
    if (stored === null) { localStorage.setItem(key, String(reached)); return; }
    if (reached > Number(stored)) {
      localStorage.setItem(key, String(reached));
      setCelebrate(true);
      toast.success(`Traguardo raggiunto! Hai superato ${euro(reached)} di risparmio`);
    }
  }, [loading, savings.total_saved]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, h] = await Promise.all([
        api.get("/client/saved", { headers: authHeaders("client") }),
        api.get("/client/claims", { headers: authHeaders("client") }),
        api.get("/client/history", { headers: authHeaders("client") }).catch(() => ({ data: {} })),
      ]);
      setSaved(s.data.offers || []);
      setClaims(c.data.claims || []);
      setSuspendedUntil(c.data.coupon_suspended_until || null);
      setSavings({ total_saved: h.data.total_saved || 0, operations: h.data.operations || 0 });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (getToken("client")) load(); else setLoading(false); /* eslint-disable-next-line */ }, []);

  const unsave = async (offerId) => {
    await api.post(`/client/saved/${offerId}`, {}, { headers: authHeaders("client") });
    setSaved((s) => s.filter((o) => o.offer_id !== offerId));
    refreshClient();
  };

  const cancelClaim = async (claimId) => {
    if (!window.confirm("Vuoi disdire questo coupon? Verrà rimosso dai tuoi coupon e il posto tornerà disponibile per altri.")) return;
    try {
      await api.delete(`/client/claims/${claimId}`, { headers: authHeaders("client") });
      setClaims((cs) => cs.filter((c) => c.claim_id !== claimId));
      window.dispatchEvent(new CustomEvent("kn-coupons-changed"));
      toast.success("Coupon disdetto");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile disdire il coupon");
    }
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;

  if (ready && !client) {
    return (
      <div className="py-16 text-center kn-fade-up" data-testid="coupons-login-required">
        <div className="h-16 w-16 rounded-3xl bg-[#FFE4E6] flex items-center justify-center mx-auto mb-3"><Ticket className="h-7 w-7 text-[#E63946]" /></div>
        <p className="font-bold text-slate-800 text-lg">Accedi per vedere i tuoi coupon</p>
        <p className="text-slate-500 text-sm mt-1">Richiedi un coupon da un'offerta per iniziare.</p>
        <button onClick={() => navigate("/")} className="mt-4 h-11 px-5 rounded-xl kn-gradient text-white font-bold">Scopri le offerte</button>
      </div>
    );
  }

  const fmtUntil = (iso) => { try { const d = new Date(iso); return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="space-y-4 kn-fade-up">
      {celebrate && <Confetti onDone={() => setCelebrate(false)} />}
      {suspendedUntil && (
        <div className="kn-card p-4 border border-amber-200 bg-amber-50" data-testid="coupon-suspension-banner">
          <div className="flex items-start gap-2.5">
            <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-extrabold text-amber-800">Richieste offerte a numero chiuso sospese</p>
              <p className="text-sm text-amber-700 mt-0.5">
                Hai lasciato scadere troppi coupon a numero chiuso senza usarli. Prendere coupon che poi non usi toglie la possibilità ad altri di approfittarne.
                Potrai richiederli di nuovo dal <b>{fmtUntil(suspendedUntil)}</b>. Intanto puoi usare i coupon che hai già o disdire quelli che non ti servono.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Savings banner */}
      <div className="w-full bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white kn-shadow-soft kn-pop" data-testid="savings-banner">
        <div className="flex items-center gap-2 text-white/85 text-sm font-semibold">
          <PiggyBank className="h-5 w-5" /> Hai risparmiato finora
        </div>
        <p className="text-5xl font-black tracking-tight mt-1 leading-none" data-testid="savings-total">{euro(savings.total_saved)}</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-white/15">
            {savings.operations} {savings.operations === 1 ? "coupon usato" : "coupon usati"}
          </span>
          {savings.total_saved > 0 ? (
            <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-white/15">
              in media {euro(savings.operations ? savings.total_saved / savings.operations : 0)} a coupon
            </span>
          ) : (
            <span className="text-white/80 text-xs font-semibold">Usa un coupon in cassa per iniziare a risparmiare!</span>
          )}
        </div>
        {(() => {
          const { goal, prev } = savingsGoal(savings.total_saved);
          const pct = Math.max(0, Math.min(100, Math.round(((savings.total_saved - prev) / (goal - prev)) * 100)));
          const missing = Math.max(0, goal - savings.total_saved);
          return (
            <div className="mt-4" data-testid="savings-goal">
              <div className="flex items-center justify-between text-xs font-semibold text-white/90 mb-1.5">
                <span>Ti mancano <b className="text-white">{euro(missing)}</b> per arrivare a {euro(goal)}</span>
                <span className="text-white/70">{pct}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-white/25 overflow-hidden">
                <div className="h-full rounded-full bg-white transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })()}
      </div>

      <Tabs defaultValue="claimed">
        <TabsList className="grid grid-cols-2 w-full h-12 rounded-xl bg-slate-100 p-1">
          <TabsTrigger value="claimed" data-testid="tab-claimed" className="rounded-lg font-bold data-[state=active]:!bg-[#E63946] data-[state=active]:!text-white">
            <Ticket className="h-4 w-4 mr-1.5" /> Richiesti
          </TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved" className="rounded-lg font-bold data-[state=active]:!bg-[#E63946] data-[state=active]:!text-white">
            <Heart className="h-4 w-4 mr-1.5" /> Salvati
          </TabsTrigger>
        </TabsList>

        <TabsContent value="claimed" className="mt-4 space-y-3">
          {claims.length === 0 ? (
            <Empty icon={Ticket} text="Nessun coupon richiesto" sub="Richiedi il tuo primo coupon dalle offerte!" />
          ) : (() => {
            const Row = (c, opts = {}) => (
              <div key={c.claim_id} data-testid={`claim-row-${c.claim_id}`} className="kn-card p-3.5 flex items-center gap-3">
                <button type="button" onClick={() => navigate(`/coupon/${c.claim_id}`)}
                  data-testid={`claim-open-${c.claim_id}`}
                  className="flex items-center gap-3 min-w-0 flex-1 text-left group">
                  <div className="h-11 w-11 rounded-xl bg-[#FFE4E6] flex items-center justify-center shrink-0 overflow-hidden">
                    {c.logo ? <img src={c.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-5 w-5 text-[#E63946]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-slate-800 truncate group-hover:text-[#E63946] transition-colors">{c.title}</p>
                      {couponValue(c) && <span className="shrink-0 text-[11px] font-extrabold text-white kn-gradient px-1.5 py-0.5 rounded-md">{couponValue(c)}</span>}
                    </div>
                    <p className="text-xs text-slate-400 truncate">{relativeDate(c.claimed_at)}</p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                      {c.min_spend > 0 && (
                        <span data-testid={`claim-minspend-${c.claim_id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600">
                          <Tag className="h-3 w-3 text-slate-400" /> Spesa min. {c.min_spend}€
                        </span>
                      )}
                      {expiryLabel(c.expires_at) && (
                        <span data-testid={`claim-expiry-${c.claim_id}`} className={`inline-flex items-center gap-1 text-[11px] font-semibold ${expiryLabel(c.expires_at) === "Scaduto" ? "text-[#E63946]" : "text-slate-600"}`}>
                          <Clock className="h-3 w-3 text-slate-400" /> {expiryLabel(c.expires_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1"><Eye className="h-3 w-3" /> Tocca per aprire il coupon</p>
                  </div>
                </button>
                <button onClick={() => setShareTarget(c)} data-testid={`share-claim-${c.claim_id}`}
                  title="Condividi" aria-label="Condividi"
                  className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors">
                  <Share2 className="h-4 w-4 text-slate-600" />
                </button>
                {c.redeemed ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-sm shrink-0"><CheckCircle2 className="h-4 w-4" /> Usato</span>
                ) : opts.expired ? (
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="inline-flex items-center gap-1 text-slate-400 font-bold text-xs"><Clock className="h-3.5 w-3.5" /> Scaduto</span>
                    <button onClick={() => cancelClaim(c.claim_id)} data-testid={`remove-claim-${c.claim_id}`}
                      className="text-[11px] font-bold text-slate-400 hover:text-[#E63946] flex items-center gap-1">
                      <X className="h-3 w-3" /> Rimuovi
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <button onClick={() => navigate(`/coupon/${c.claim_id}?show=1`)} data-testid={`show-qr-${c.claim_id}`}
                      className="h-10 px-3 rounded-xl kn-gradient text-white font-bold text-sm flex items-center gap-1.5">
                      <QrCode className="h-4 w-4" /> Mostra
                    </button>
                    <button onClick={() => cancelClaim(c.claim_id)} data-testid={`cancel-claim-${c.claim_id}`}
                      className="text-[11px] font-bold text-slate-400 hover:text-[#E63946] flex items-center gap-1">
                      <X className="h-3 w-3" /> Disdici
                    </button>
                  </div>
                )}
              </div>
            );

            const unusedPanel = (list) => list.length > 0 && (
              <div className="pt-1">
                <button onClick={() => setShowUnused((s) => !s)} data-testid="toggle-unused-coupons"
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-amber-50 text-amber-700 font-bold text-sm">
                  <span>Non utilizzati ({list.length})</span>
                  {showUnused ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {showUnused && (
                  <div className="mt-3 space-y-3">
                    <div className="kn-card p-3.5 border border-amber-200 bg-amber-50/70 flex items-start gap-2.5" data-testid="unused-coupons-banner">
                      <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 leading-relaxed">
                        I coupon a numero chiuso richiesti e non usati tolgono il posto ad altre persone. Richiederne troppi senza usarli può portare a una sospensione temporanea. Richiedi solo ciò che userai davvero 🙏
                      </p>
                    </div>
                    <div className="opacity-80 space-y-3">{list.map((c) => Row(c, { expired: true }))}</div>
                  </div>
                )}
              </div>
            );

            // Group coupons by store
            const storesMap = {};
            for (const c of claims) {
              const vid = c.vendor_id || c.store_name || "unknown";
              if (!storesMap[vid]) storesMap[vid] = { vendor_id: vid, store_name: c.store_name || "Negozio", logo: c.logo || "", coupons: [] };
              storesMap[vid].coupons.push(c);
            }
            const stores = Object.values(storesMap).map((s) => ({
              ...s,
              active: s.coupons.filter((c) => !c.redeemed && !isExpired(c)).length,
              unused: s.coupons.filter((c) => !c.redeemed && isExpired(c)).length,
              used: s.coupons.filter((c) => c.redeemed).length,
              lastAt: s.coupons.reduce((m, c) => (c.claimed_at > m ? c.claimed_at : m), ""),
            })).sort((a, b) => (b.lastAt > a.lastAt ? 1 : -1));

            // Store detail view
            if (selectedStore) {
              const store = stores.find((s) => s.vendor_id === selectedStore);
              if (!store) return null;
              const active = store.coupons.filter((c) => !c.redeemed && !isExpired(c));
              const unused = store.coupons.filter((c) => !c.redeemed && isExpired(c));
              const used = store.coupons.filter((c) => c.redeemed);
              return (
                <>
                  <button onClick={() => setSelectedStore(null)} data-testid="coupons-store-back"
                    className="flex items-center gap-1.5 text-sm font-bold text-[#E63946]"><ChevronLeft className="h-4 w-4" /> Tutti i negozi</button>
                  <div className="flex items-center gap-3 px-1">
                    <div className="h-12 w-12 rounded-2xl bg-[#FFE4E6] flex items-center justify-center shrink-0 overflow-hidden">
                      {store.logo ? <img src={store.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-6 w-6 text-[#E63946]" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-extrabold text-slate-900 truncate">{store.store_name}</p>
                      <p className="text-xs text-slate-400">{active.length} disponibili · {used.length} usati{unused.length > 0 ? ` · ${unused.length} non usati` : ""}</p>
                    </div>
                  </div>
                  {active.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nessun coupon disponibile.</p>}
                  {active.map(Row)}
                  {unusedPanel(unused)}
                  {used.length > 0 && (
                    <div className="pt-1">
                      <button onClick={() => setShowUsed((s) => !s)} data-testid="toggle-used-coupons"
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-50 text-slate-600 font-bold text-sm">
                        <span>Coupon già usati ({used.length})</span>
                        {showUsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      {showUsed && <div className="mt-3 space-y-3 opacity-75">{used.map(Row)}</div>}
                    </div>
                  )}
                </>
              );
            }

            // Store list view
            const sortControl = (
              <div className="flex gap-2" data-testid="coupon-sort">
                <button type="button" onClick={() => setSortMode("store")} data-testid="sort-store"
                  className={`flex-1 h-9 rounded-xl text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-colors ${sortMode === "store" ? "kn-gradient text-white" : "bg-slate-100 text-slate-600"}`}>
                  <Store className="h-4 w-4" /> Per negozio
                </button>
                <button type="button" onClick={() => setSortMode("expiry")} data-testid="sort-expiry"
                  className={`flex-1 h-9 rounded-xl text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-colors ${sortMode === "expiry" ? "kn-gradient text-white" : "bg-slate-100 text-slate-600"}`}>
                  <Clock className="h-4 w-4" /> Per scadenza
                </button>
              </div>
            );

            const expTime = (c) => (c.expires_at ? new Date(c.expires_at).getTime() : Infinity);

            // Sort-by-expiry: flat list of all coupons, soonest expiry first
            if (sortMode === "expiry") {
              const activeAll = claims.filter((c) => !c.redeemed && !isExpired(c)).sort((a, b) => expTime(a) - expTime(b));
              const unusedAll = claims.filter((c) => !c.redeemed && isExpired(c)).sort((a, b) => expTime(b) - expTime(a));
              const usedAll = claims.filter((c) => c.redeemed).sort((a, b) => (b.claimed_at > a.claimed_at ? 1 : -1));
              return (
                <>
                  {sortControl}
                  {activeAll.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nessun coupon disponibile.</p>}
                  {activeAll.map(Row)}
                  {unusedPanel(unusedAll)}
                  {usedAll.length > 0 && (
                    <div className="pt-1">
                      <button onClick={() => setShowUsed((s) => !s)} data-testid="toggle-used-coupons"
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-50 text-slate-600 font-bold text-sm">
                        <span>Coupon già usati ({usedAll.length})</span>
                        {showUsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      {showUsed && <div className="mt-3 space-y-3 opacity-75">{usedAll.map(Row)}</div>}
                    </div>
                  )}
                </>
              );
            }

            const q = couponQ.trim().toLowerCase();
            const shown = stores.filter((s) => !q || s.store_name.toLowerCase().includes(q));
            const unusedAll = claims.filter((c) => !c.redeemed && isExpired(c));
            return (
              <>
                {sortControl}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input data-testid="coupon-search" value={couponQ} onChange={(e) => setCouponQ(e.target.value)} placeholder="Cerca un negozio…"
                    className="w-full h-11 pl-10 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946]/30" />
                </div>
                {shown.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nessun negozio trovato.</p>}
                {shown.map((s) => (
                  <button key={s.vendor_id} type="button" onClick={() => { setSelectedStore(s.vendor_id); setShowUsed(false); setShowUnused(false); }}
                    data-testid={`coupons-store-${s.vendor_id}`}
                    className="w-full kn-card p-3.5 flex items-center gap-3 text-left hover:ring-1 hover:ring-[#E63946]/30 transition">
                    <div className="h-12 w-12 rounded-2xl bg-[#FFE4E6] flex items-center justify-center shrink-0 overflow-hidden">
                      {s.logo ? <img src={s.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-6 w-6 text-[#E63946]" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-800 truncate">{s.store_name}</p>
                      <p className="text-xs text-slate-400">
                        {s.active > 0 ? <span className="text-[#E63946] font-semibold">{s.active} {s.active === 1 ? "coupon disponibile" : "coupon disponibili"}</span> : "Nessun coupon attivo"}
                        {s.used > 0 && ` · ${s.used} usati`}
                      </p>
                    </div>
                    {s.active > 0 && <span className="h-6 min-w-6 px-1.5 rounded-full kn-gradient text-white text-xs font-extrabold flex items-center justify-center shrink-0">{s.active}</span>}
                    <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
                  </button>
                ))}
                {!q && unusedPanel(unusedAll)}
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="saved" className="mt-4">
          {saved.length === 0 ? (
            <Empty icon={Heart} text="Nessuna offerta salvata" sub="Tocca il cuore su un'offerta per salvarla." />
          ) : (
            <div className="grid grid-cols-2 gap-3 auto-rows-fr">
            {saved.map((o) => <OfferCard key={o.offer_id} offer={o} saved onToggleSave={unsave} dimmed={o.status !== "active"} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {shareTarget && (
        <ShareSheet open={!!shareTarget} onOpenChange={(o) => { if (!o) setShareTarget(null); }}
          url={`${typeof window !== "undefined" ? window.location.origin : ""}/offer/${shareTarget.offer_id}`}
          title={shareTarget.title} storeName={shareTarget.store_name} />
      )}
    </div>
  );
}

function Empty({ icon: Icon, text, sub }) {
  return (
    <div className="text-center py-14">
      <div className="h-16 w-16 rounded-3xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
        <Icon className="h-7 w-7 text-slate-400" />
      </div>
      <p className="font-bold text-slate-700">{text}</p>
      <p className="text-slate-400 text-sm mt-1">{sub}</p>
    </div>
  );
}
